import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useLoaderStore } from './loader.store';
import _ from 'lodash';
import * as topicsApi from '../api/topics.api';
import { useMetricsStore } from './metrics.store';

export const useTopicsStore = defineStore('topics', () => {
    const topics = ref(null);
    const loader = useLoaderStore();

    const totalSize = ref(0);
    const page = ref(0);
    const pageSize = ref(0);

    const statistics = ref(null);
    const lastSearchQuery = ref(null);
    const topicConfigs = ref(null);
    const messages = ref({});

    const messagesPage = ref(0);
    const messagesPageSize = ref(0);
    const messagesPageTotal = ref(0);
    const messagesPageSort = ref('asc');
    const messagesHasNext = ref(null);

    const lastSeenOffsets = ref({});
    const firstSeenOffsets = ref({});

    return {
        total: computed(() => totalSize.value),
        page: computed(() => page.value),
        pageSize: computed(() => pageSize.value),
        messagesPage: computed(() => messagesPage.value),
        messagesPageSize: computed(() => messagesPageSize.value),
        messagesPageTotal: computed(() => messagesPageTotal.value),
        messagesHasNext: computed(() => messagesHasNext.value),
        lastSeenOffsets: computed(() => lastSeenOffsets.value),
        topics,
        configs: computed(() => topicConfigs.value),
        statistics: computed(() => statistics.value),
        // Getters
        all: computed(() => topics.value),
        // Actions
        fetch: async function ({ page_size, page_number, partial_name } = {}) {
            // TODO - enable once we have real time updates
            // if (
            //     !partial_name &&
            //     partial_name !== '' &&
            //     !lastSearchQuery.value &&
            //     lastSearchQuery.value !== '' &&
            //     page_number === page.value &&
            //     !_.isEmpty(topics.value) &&
            //     pageSize.value === page_size
            // )
            //     return topicstopics.value;

            loader.activateLoading('topics.fetch');

            return topicsApi
                .fetchTopics({ page_size, page_number: page_number + 1, partial_name })
                .then(({ result, total }) => {
                    topics.value = _.isEmpty(result || []) ? [] : result;

                    page.value = page_number;
                    pageSize.value = page_size;
                    totalSize.value = total;
                    lastSearchQuery.value = partial_name;

                    const metricsStore = useMetricsStore();

                    const grouped = _.groupBy(topics.value, 'entity.entity_id');

                    const topicTableMetricsPayload = _.map(grouped, (topics_per_entity, entity_id) => {
                        return {
                            id: entity_id,
                            entity_type: topics_per_entity[0]?.entity?.entity_type || 'sources',
                            connector: topics_per_entity[0]?.entity?.connector,
                            topic_ids: topics_per_entity[0]?.entity?.topic_ids,
                            topic_db_ids: topics_per_entity[0]?.entity?.topic_db_ids
                        };
                    });

                    metricsStore.topics.fetchTableMetrics(topicTableMetricsPayload);

                    return result;
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => {
                    loader.deactivateLoading('topics.fetch');
                });
        },
        fetchTopicConfigs: async () => {
            loader.activateLoading('topics.fetchTopicConfigs');
            try {
                if (topicConfigs.value && !_.isEmpty(topicConfigs.value)) {
                    return topicConfigs.value;
                }

                const data = await topicsApi.fetchTopicConfigs();
                topicConfigs.value = data;

                return data;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('topics.fetchTopicConfigs');
            }
        },
        fetchTopicStatistics: async () => {
            loader.activateLoading('topics.fetchTopicStatistics');
            try {
                // if (statistics.value && !_.isEmpty(statistics.value)) {
                //     return statistics.value;
                // }

                const data = await topicsApi.fetchTopicStatistics();
                statistics.value = data;

                return data;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('topics.fetchTopicStatistics');
            }
        },
        getById: async function (topic_id, forceFetch = false) {
            if (!topic_id) {
                console.error('Topic ID is required');
                return;
            }

            if (!forceFetch) {
                const found = _.find(topics.value, { id: topic_id });

                if (found) {
                    return found;
                }
            }

            loader.activateLoading('topics.getById');

            try {
                const topic = await topicsApi.fetchTopic(topic_id);

                if (!topics?.value) {
                    topics.value = [topic];
                } else {
                    const foundIndex = _.findIndex(topics.value, { id: topic_id });

                    if (foundIndex > -1) {
                        topics.value[foundIndex] = topic;
                    } else {
                        topics.value.push(topic);
                    }
                }

                const metricsStore = useMetricsStore();

                const grouped = _.groupBy([topic], 'entity.entity_id');

                const topicTableMetricsPayload = _.map(grouped, (topics_per_entity, entity_id) => {
                    return {
                        id: entity_id,
                        entity_type: topics_per_entity[0]?.entity?.entity_type || 'sources',
                        connector: topics_per_entity[0]?.entity?.connector,
                        topic_ids: topics_per_entity[0]?.entity?.topic_ids,
                        topic_db_ids: topics_per_entity[0]?.entity?.topic_db_ids
                    };
                });

                await metricsStore.topics.fetchTableMetrics(topicTableMetricsPayload);

                return topic;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('topics.getById');
            }
        },
        fetchTopicMessages: async function (
            id,
            {
                page_size = 10,
                page = 0,
                sort = 'asc',
                direction = null,
                seek_type = null,
                seek_value = null,
                regex = null,
                regex_search_fields = null
            } = {}
        ) {
            loader.activateLoading('topics.getMessagesForTopic');

            // if (messages.value[id] && messages.value[id][page]?.messages?.length && messages.value[id][page]?.sort === sort) {
            //     if (messagesPage.value === page && messagesPageSize.value === page_size) {
            //         return messages.value[id][page]?.messages;
            //     }
            // }

            // if we request first page, we need to send last_seen_offsets at all
            let first_seen_offsets = null;

            const last_seen_offsets = page === 0 ? null : JSON.stringify(lastSeenOffsets.value);

            if (direction === 'backward') {
                first_seen_offsets = page === 0 ? null : JSON.stringify(firstSeenOffsets.value);
            }

            return topicsApi
                .fetchTopicMessages(id, {
                    page_size,
                    page: page + 1,
                    sort,
                    seek_type,
                    seek_value,
                    direction,
                    regex,
                    regex_search_fields,
                    last_seen_offsets,
                    first_seen_offsets
                })
                .then(({ result, total, has_next, last_seen_offsets, first_seen_offsets }) => {
                    messages.value[id] = {
                        [page]: {
                            messages: _.isEmpty(result || []) ? [] : result,
                            sort
                        }
                    };

                    messagesPage.value = page;
                    messagesPageSize.value = page_size;
                    messagesPageTotal.value = total;
                    messagesHasNext.value = _.isBoolean(has_next) ? has_next : null;
                    messagesPageSort.value = sort;
                    lastSeenOffsets.value = last_seen_offsets || {};
                    firstSeenOffsets.value = first_seen_offsets || {};

                    return result;
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => {
                    loader.deactivateLoading('topics.getMessagesForTopic');
                });
        },
        getMessageById: async function (id) {
            if (!id) return;

            const found = _.find(messages.value, { id });

            if (found) {
                return found;
            }
        },
        setTopicTableMetricsAction: async function (metrics) {
            topics.value = topics.value?.map((topic) => ({
                ...topic,
                metrics: {
                    ...topic.metrics,
                    ...(metrics[topic.id]?.metrics || {})
                }
            }));
        },
        update: async function (topic_id, data) {
            loader.activateLoading('topics.update');
            loader.activateLoading(`topics.update.${topic_id}`);

            return topicsApi
                .updateTopic(topic_id, data)
                .then(async () => {
                    await this.getById(topic_id, true);
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => {
                    loader.deactivateLoading('topics.update');
                    loader.deactivateLoading(`topics.update.${topic_id}`);
                });
        },
        /**
         * Delete topic by id (name) and refresh list.
         * @param {string} topic_id
         */
        delete: async function (topic_id) {
            loader.activateLoading('topics.delete');
            try {
                await topicsApi.deleteTopic(topic_id);
                // Refresh current list
                this.fetch({ page_size: pageSize.value, page_number: page.value, partial_name: lastSearchQuery.value });
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('topics.delete');
            }
        }
    };
});
